import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import styles from './InReviewProducts.module.scss';
import { AllProductsProps } from 'enums/AllProductsEnums';
import COProductCard from 'shared/components/COProductCard';
import COAlertModal from 'shared/components/COAlertModal';
import { useNavigate, generatePath } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { ProductsEnums } from 'enums/ProductsEnums';
import COPagination from 'shared/components/COPagination';
import COSearch from 'shared/components/COSearch';
import { SearchProps } from 'enums/ProductsViewEnums';
import { searchUtil } from 'shared/utils/SearchUtil';
import CreateProductModal from 'shared/components/CreateProductModal';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import EmptyStateComponent from 'shared/components/EmptyStateComponent';
import { addProductSubTitle, addProductTitle } from 'constants/constants';
import { ProductsProps } from '../AllProducts';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import ProductCreationService from 'services/ProductsService/productCreation.service';

const InReviewProducts: FC<ProductsProps> = ({
  processingFacilityList,
  handleModalOpen,
}) => {
  const {
    fetchProducts,
    productList,
    deleteProduct,
    duplicateProduct,
    totalItems,
    currentPage,
    loading,
  } = ProductCreationService();
  const user = localStorage.getItem('userDetails');
  let parsedUser: { id: number } | null = null;
  if (user !== null) {
    parsedUser = JSON.parse(user);
  }

  const [clickedCard, setClickedCard] = useState<number | undefined>();
  const [createProductModalState, setCreateProductModalState] =
    useState<boolean>(false);
  const [showDeleteModalState, setDeleteModalState] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [showCopyModalState, setCopyModalState] = useState<boolean>(false);
  const navigate = useNavigate();
  const { organizationData } = useParentOrganizationContext();
  /** Handle Open/Close states for Delete model */
  const handleOpenDeleteModal = () => {
    setDeleteModalState(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalState(false);
  };

  const handleModalCancel = () => {
    setCopyModalState(false);
    setDeleteModalState(false);
    setClickedCard(undefined);
  };

  const handleModalConfirm = async () => {
    handleCloseCopyModal();
    handleCloseDeleteModal();
    clickedCard && (await deleteProduct(clickedCard));
    setClickedCard(undefined);
  };

  const handleModalConfirmCopy = async () => {
    clickedCard && (await duplicateProduct(clickedCard));
    setClickedCard(undefined);
    handleCloseCopyModal();
  };
  const handleOpenCreateProductModal = () => {
    if (
      processingFacilityList?.length &&
      organizationData?.organization?.name
    ) {
      setCreateProductModalState(true);
    } else {
      handleModalOpen?.(true);
    }
  };

  const handleCloseCreateProductModal = () => {
    setCreateProductModalState(false);
  };

  const onDeleteClick = (value: number | undefined) => {
    handleOpenDeleteModal();
    handleRowClick(value);
  };

  const handleRowClick = (value: number | undefined) => {
    setClickedCard(value);
  };

  // Handle Copy
  const handleOpenCopyModal = () => {
    setCopyModalState(true);
  };

  const handleCloseCopyModal = () => {
    setCopyModalState(false);
  };

  const onCopyClick = (value: number | undefined) => {
    handleOpenCopyModal();
    handleRowClick(value);
  };
  const navigateProductOverview = (productId: number | undefined) => {
    navigate(generatePath(AppRoutes.PRODUCT_OVERVIEW, { id: productId }));
  };
  useEffect(() => {
    fetchProducts({ page: 1, size: 24, status: ProductsEnums.IN_REVIEW });
  }, []);
  const handlePaginationChange = (page: number, pageSize: number) => {
    fetchProducts({
      page: page,
      size: pageSize,
      status: ProductsEnums.IN_REVIEW,
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    const searchValue = e.target.value;
    searchUtil((searchVal: string) => {
      fetchProducts({ search: searchVal, status: ProductsEnums.IN_REVIEW });
    })(searchValue);
  };
  return (
    <>
      <div className={styles.searchContainer}>
        <COSearch
          name={SearchProps.SEARCH_NAME}
          placeholderString={SearchProps.PLACEHOLDER_STRING}
          inputValue={searchValue}
          handleChange={handleInputChange}
        />
      </div>
      <div className={styles.PaginationContainer}>
        <COPagination
          onChange={handlePaginationChange}
          total={totalItems}
          current={currentPage}
        />
      </div>

      {createProductModalState && (
        <CreateProductModal
          showModalState={createProductModalState}
          handleModalCancel={handleCloseCreateProductModal}
          handleModalConfirm={handleCloseCreateProductModal}
          userId={Number(parsedUser?.id)}
          isCreate={true}
        />
      )}

      <div className={styles.inReviewProductsContainer}>
        {productList.length > 0 ? (
          <div className={styles.productContainer}>
            {productList.map((product: any, index: number) => (
              <COProductCard
                key={index}
                calenderRow
                data={product}
                multiRowCard
                onDeleteClick={onDeleteClick}
                onCopyClick={onCopyClick}
                navigateProductOverview={navigateProductOverview}
                isProduct={true}
              />
            ))}
          </div>
        ) : (
          <>
            {loading ? (
              <div className={styles.loaderContainer}>
                <LoaderAnimation />
              </div>
            ) : (
              <div className={styles.buttonContainer}>
                <div onClick={handleOpenCreateProductModal}>
                  <EmptyStateComponent
                    title={addProductTitle}
                    subTitle={addProductSubTitle}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <COAlertModal
        handleModalCancel={handleModalCancel}
        handleModalConfirm={handleModalConfirm}
        showModalState={showDeleteModalState}
        deleteModal
        heading={AllProductsProps.DELETE_PRODUCT}
        subHeading={AllProductsProps.DELETE_SUBHEADING}
      />
      <COAlertModal
        handleModalCancel={handleModalCancel}
        handleModalConfirm={handleModalConfirmCopy}
        showModalState={showCopyModalState}
        heading={AllProductsProps.COPY_PRODUCT}
        subHeading={AllProductsProps.COPY_SUBHEADING}
      />
    </>
  );
};

export default InReviewProducts;
